// Physician sign queue, signing panel (typed + drawn signature), and the signed-record block.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorBox } from '../components/Layout.tsx';
import { post, type Me } from '../lib/api.ts';
import { useData } from '../lib/useData.ts';

export interface Review {
  respiratorQualified: boolean | null; referred: boolean | null; referralReason: string | null; notes: string | null;
  signerName: string; signerCredential: string | null; typedSignature: string; signatureImage: string | null;
  signedAt: string; sha256: string;
  /** electronic (signed in the app), paper (recorded from a paper-signed form), staff (no physician signature required). */
  signatureKind?: 'electronic' | 'paper' | 'staff';
}

interface QueueRow { id: string; assessedOn: string; firstName: string; lastName: string; departmentName: string | null; readySince: string; correctsId: string | null; confirmedChecks: number }

export function SignQueue() {
  const { data, error } = useData<QueueRow[]>('/api/reviews/queue');
  const nav = useNavigate();
  return (
    <>
      <h1>Sign queue</h1>
      <p className="muted">Assessments reviewed by the office and waiting for a physician, oldest first.</p>
      <ErrorBox error={error} />
      <div className="card">
        {!data?.length ? <p className="muted">Nothing waiting. 🎉</p> : (
          <table><thead><tr><th>Assessed</th><th>Participant</th><th>Organization</th><th>Ready since</th><th /></tr></thead>
            <tbody>{data.map((r) => (
              <tr key={r.id} className="link" onClick={() => nav(`/assessments/${r.id}`)}>
                <td>{r.assessedOn}</td><td>{r.lastName}, {r.firstName}</td><td>{r.departmentName ?? '—'}</td>
                <td className="small">{new Date(r.readySince).toLocaleString()}</td>
                <td>{r.correctsId && <span className="badge provisional">correction</span>}
                  {r.confirmedChecks > 0 && <span className="badge reviewed" title="Unusual values confirmed as correct: shown on the visit" style={{ marginLeft: 4 }}>{r.confirmedChecks} unusual value{r.confirmedChecks === 1 ? '' : 's'}</span>}</td></tr>))}</tbody></table>
        )}
      </div>
    </>
  );
}

/** Handwriting-style fonts the device may have (no web fonts are loaded); falls back to the browser's cursive. */
const SCRIPT_FONT = '"Segoe Script", "Brush Script MT", "Lucida Handwriting", "Apple Chancery", "Snell Roundhand", cursive';

/**
 * Signature pad. While the physician has not drawn anything, the typed name is shown as a signature and used as the
 * image. Drawing replaces it; Clear empties the box for drawing; "Use typed name" goes back. Returns a PNG data URL.
 */
function SignaturePad({ typed, onChange }: { typed: string; onChange: (dataUrl: string | null) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [mode, setMode] = useState<'typed' | 'drawn' | 'empty'>('typed');
  const ctx = () => {
    const c = ref.current!.getContext('2d')!;
    c.lineWidth = 2.2; c.lineCap = 'round'; c.lineJoin = 'round'; c.strokeStyle = '#111';
    return c;
  };
  const wipe = () => { const c = ref.current!; c.getContext('2d')!.clearRect(0, 0, c.width, c.height); };

  // Typed mode: render the name, shrinking the font until it fits the box.
  useEffect(() => {
    if (mode !== 'typed') return;
    wipe();
    const name = typed.trim();
    if (!name) { onChange(null); return; }
    const c = ref.current!, g = c.getContext('2d')!;
    let size = 64;
    do { g.font = `${size}px ${SCRIPT_FONT}`; size -= 2; } while (g.measureText(name).width > c.width - 40 && size > 18);
    g.fillStyle = '#10204a'; g.textBaseline = 'middle';
    g.fillText(name, 20, c.height / 2 + 4);
    onChange(c.toDataURL('image/png'));
  }, [typed, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const pos = (e: React.PointerEvent) => {
    const r = ref.current!.getBoundingClientRect();
    return [(e.clientX - r.left) * (ref.current!.width / r.width), (e.clientY - r.top) * (ref.current!.height / r.height)] as const;
  };
  return (
    <div>
      <canvas ref={ref} width={600} height={150} aria-label="Signature: your typed name, or draw your own"
        style={{ width: '100%', maxWidth: 600, height: 150, border: '1px dashed #999', borderRadius: 6, touchAction: 'none', background: '#fff' }}
        onPointerDown={(e) => {
          if (mode === 'typed') wipe();   // drawing replaces the typed signature
          setMode('drawn');
          drawing.current = true; const [x, y] = pos(e); const g = ctx(); g.beginPath(); g.moveTo(x, y); ref.current!.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => { if (!drawing.current) return; const [x, y] = pos(e); const g = ctx(); g.lineTo(x, y); g.stroke(); }}
        onPointerUp={() => { drawing.current = false; onChange(ref.current!.toDataURL('image/png')); }}
      />
      <div className="small">
        <button type="button" className="link" onClick={() => { wipe(); setMode('empty'); onChange(null); }}>Clear</button>
        {mode !== 'typed' && <> · <button type="button" className="link" onClick={() => setMode('typed')}>Use typed name</button></>}
        <span className="muted"> {mode === 'typed' ? 'Your typed name signs here. To use your own, draw in the box (or Clear first).' : 'Draw with a finger, stylus or mouse.'}</span>
      </div>
    </div>
  );
}

const yn = (v: boolean | null) => (v === true ? 'yes' : v === false ? 'no' : '');

/** initial: for a correction, the decisions on the original (the physician confirms or changes them). */
export function SignPanel({ assessmentId, me, onSigned, initial }: {
  assessmentId: string; me: Me; onSigned: () => void;
  initial?: { respiratorQualified: boolean | null; referred: boolean | null; referralReason: string | null; notes: string | null };
}) {
  const [respirator, setRespirator] = useState<boolean | null>(initial ? initial.respiratorQualified : null);
  const [referred, setReferred] = useState<boolean | null>(initial ? initial.referred : false);
  const [reason, setReason] = useState(initial?.referralReason ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [typed, setTyped] = useState('');
  const [image, setImage] = useState<string | null>(null);
  const [attest, setAttest] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await post(`/api/reviews/${assessmentId}/sign`, {
        respiratorQualified: respirator, referred, referralReason: referred ? reason : null, notes: notes || null,
        typedSignature: typed, signatureImage: image, attest,
      });
      onSigned();
    } catch (x) { setError(x); } finally { setBusy(false); }
  };
  const sel = (v: boolean | null, set: (v: boolean | null) => void, id: string) => (
    <select id={id} value={yn(v)} onChange={(e) => set(e.target.value === '' ? null : e.target.value === 'yes')}>
      <option value="">—</option><option value="yes">Yes</option><option value="no">No</option>
    </select>
  );
  return (
    <form className="card" onSubmit={submit} style={{ borderColor: 'var(--ngf-ink)' }}>
      <h2>Physician review and signature</h2>
      <ErrorBox error={error} />
      <div className="row">
        <div className="field"><label htmlFor="resp">Respirator qualified</label>{sel(respirator, setRespirator, 'resp')}</div>
        <div className="field"><label htmlFor="ref">Referred</label>{sel(referred, setReferred, 'ref')}</div>
      </div>
      {referred && <div className="field"><label htmlFor="reason">Referral reason</label>
        <input id="reason" value={reason} onChange={(e) => setReason(e.target.value)} required /></div>}
      <div className="field"><label htmlFor="notes">Notes</label>
        <textarea id="notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
      <div className="field"><label htmlFor="typed">Type your name to sign</label>
        <input id="typed" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={me.displayName} required /></div>
      <div className="field"><SignaturePad typed={typed} onChange={setImage} /></div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontWeight: 400 }}>
        <input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} />
        <span>I, {me.displayName}{me.credential ? `, ${me.credential}` : ''}, reviewed this assessment and its results and sign it electronically.
          I understand the signed record cannot be changed; corrections create a new version.</span>
      </label>
      <div className="actions" style={{ marginTop: '0.75rem' }}><button disabled={busy || !attest || !typed.trim()}>Sign</button></div>
    </form>
  );
}

export function SignatureBlock({ review }: { review: Review }) {
  if (review.signatureKind === 'staff') return (
    <div className="card" style={{ borderColor: 'var(--ok)' }}>
      <h2>Reviewed by NGF staff</h2>
      <p>No physician signature required. Completed by {review.signerName} on {new Date(review.signedAt).toLocaleString()}.</p>
      <div className="small muted mono">SHA-256 {review.sha256}</div>
    </div>
  );
  return (
    <div className="card" style={{ borderColor: 'var(--ok)' }}>
      <h2>Physician review</h2>
      <div className="row">
        <div><div className="muted small">Respirator qualified</div><strong>{review.respiratorQualified === null ? '—' : review.respiratorQualified ? 'Yes' : 'No'}</strong></div>
        <div><div className="muted small">Referred</div><strong>{review.referred ? `Yes — ${review.referralReason ?? ''}` : review.referred === false ? 'No' : '—'}</strong></div>
      </div>
      {review.notes && <p><strong>Notes:</strong> {review.notes}</p>}
      <div style={{ marginTop: '0.75rem' }}>
        {review.signatureImage && <img src={review.signatureImage} alt="Signature" style={{ maxHeight: 70, display: 'block' }} />}
        <div style={{ fontFamily: 'cursive, serif', fontSize: '1.3rem' }}>/s/ {review.typedSignature}</div>
        <div className="small">{review.signerName}{review.signerCredential ? `, ${review.signerCredential}` : ''} · signed {new Date(review.signedAt).toLocaleString()}</div>
        <div className="small muted mono">SHA-256 {review.sha256}</div>
      </div>
    </div>
  );
}
